import React, { useEffect, useRef } from 'react'

import { useDropTarget, useSetState, useWatchEntityUpdates } from '@domain-group/react-hooks'

import usePublicationBookingItem from '@hooks/usePublicationBookingItem'
import type { UsePublicationBookingItemPayload } from '@hooks/usePublicationBookingItem'

import type {
  LayoutTemplateModel,
  PublicationBookingItemModel,
  PublicationBookingModel,
} from '@models/types'

const watchEntityKeys = ['publicationBookingItems']

// Leave this value low so that it still works for smaller modules
const overlapRequired = 0.1

const requestOptions = {
  publicationBookingItem: {
    include_artwork_preview: true,
    include_publication_booking_item_artwork: true,
    include_publication_booking_item_grid: true,
  },
}

type DefaultState = {
  cellSize: {
    width: number,
    height: number,
  },
  contentGrid: {
    x: number,
    y: number,
    width: number,
    height: number,
    columns: number,
    rows: number,
  },
  dropPosition?: {
    x?: number,
    y?: number,
    column?: number,
    row?: number,
    moduleColumns?: number,
    moduleRows?: number,
  },
}

type SetState<S> = (state: Partial<S>) => void

const defaultState: DefaultState = {
  cellSize: {
    width: 0,
    height: 0,
  },
  contentGrid: {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    columns: 0,
    rows: 0,
  },
  dropPosition: {},
}

type ContentGridStyles = {
  draggingOver: string,
  dropValid: string,
  hidden: string,
}

type OnDragEnterParams = {
  e: Event,
  stateRef: React.MutableRefObject<DefaultState>,
  styles: ContentGridStyles,
}

const onDragEnter = (params: OnDragEnterParams) => {
  // @ts-expect-error TS2339
  const { e: { relatedTarget }, stateRef, styles } = params

  const { cellSize } = stateRef.current

  // Set Draggable Size to reflect module
  const width = cellSize.width * Number(relatedTarget.getAttribute('data-columns'))
  const height = cellSize.height * Number(relatedTarget.getAttribute('data-rows'))

  // Add dragging class
  relatedTarget.classList.add(styles.draggingOver)

  relatedTarget.style.width = `${width}px`
  relatedTarget.style.height = `${height}px`
}

type OnDragLeaveParams = {
  e: Event,
  styles: ContentGridStyles,
}

const onDragLeave = (params: OnDragLeaveParams) => {
  // @ts-expect-error TS2339
  const { e: { relatedTarget }, styles } = params

  // Add dragging class
  relatedTarget.classList.remove(styles.draggingOver)

  relatedTarget.style.width = '287px'
  relatedTarget.style.height = '46px'
}

type ValidateDropPositionParams = {
  bookingItemId: number,
  dropPosition: DefaultState['dropPosition'],
  publicationBookingItems: PublicationBookingItemModel[],
}

const validateDropPosition = (params: ValidateDropPositionParams) => {
  const {
    bookingItemId,
    dropPosition,
    publicationBookingItems,
  } = params

  let isValid = true

  // Check if we already have a module here
  publicationBookingItems.some((publicationBookingItem) => {
    // Module overlaps self
    if (publicationBookingItem.id === bookingItemId){
      return false
    }

    // Module overlaps another one
    // if (RectA.X1 < RectB.X2 && RectA.X2 > RectB.X1 && RectA.Y1 < RectB.Y2 && RectA.Y2 > RectB.Y1)
    // module = RectA, dropPosition = RectB
    if (
      // @ts-expect-error TS18048
      publicationBookingItem.column < dropPosition.column + Number(dropPosition.moduleColumns)
      // @ts-expect-error TS18048
      && dropPosition.column < publicationBookingItem.column + Number(publicationBookingItem.grid.columns)
      // @ts-expect-error TS18048
      && publicationBookingItem.row < dropPosition.row + Number(dropPosition.moduleRows)
      // @ts-expect-error TS18048
      && dropPosition.row < publicationBookingItem.row + Number(publicationBookingItem.grid.rows)
    ){
      isValid = false
      return true
    }
    return false
  })

  return isValid
}

type CalculateValidDropPositionParams = {
  dragX: number,
  dragY: number,
  moduleColumns: number,
  moduleRows: number,
  stateRef: React.MutableRefObject<DefaultState>,
}

const calculateValidDropPosition = (params: CalculateValidDropPositionParams) => {
  const {
    dragX,
    dragY,
    moduleColumns,
    moduleRows,
    stateRef,
  } = params

  const {
    contentGrid,
    contentGrid: {
      // @ts-expect-error TS2339
      module_positioning,
    },
    cellSize,
  } = stateRef.current

  let dropPosition

  // Calculate how many modules fit in the contentGrid
  // For a 1/4 Page in a 12x6 grid we can fit 2 wide and 2 high
  // The valid positions would be: (0,0), (0,6), (3,0), (3,6)
  const anyPosition = module_positioning === 'unlocked'

  const moduleSize = {
    columns: anyPosition ? 1 : moduleColumns,
    rows: anyPosition ? 1 : moduleRows,
  }

  // The fit object calculates how many times a module can fit
  // e.g. 6 columns / moduleDivisor width of 3 would have fit value of 2
  // If we allow any position then we have all columns and rows as valid positions
  const fit = {
    width: contentGrid.columns / moduleSize.columns,
    height: contentGrid.rows / moduleSize.rows,
  }

  for (let x = 0; x < fit.width; x += 1){
    for (let y = 0; y < fit.height; y += 1){
      // Adjust the values for better usability
      const moduleWidth = moduleColumns * cellSize.width
      const moduleHeight = moduleRows * cellSize.height

      const isFullHeight = Math.floor(moduleHeight) === Math.floor(contentGrid.height)
      const isFullWidth = Math.floor(moduleWidth) === Math.floor(contentGrid.width)

      // Full Page Module - Return early
      if (isFullHeight && isFullWidth){
        dropPosition = {
          x: 0,
          y: 0,
          column: 0,
          row: 0,
          moduleColumns,
          moduleRows,
        }
        break
      }

      const position = {
        x: isFullWidth ? 0 : x * moduleSize.columns * cellSize.width,
        y: isFullHeight ? 0 : y * moduleSize.rows * cellSize.height,
        column: isFullWidth ? 0 : x * moduleSize.columns,
        row: isFullHeight ? 0 : y * moduleSize.rows,
        moduleColumns,
        moduleRows,
      }

      const adjustedX = isFullWidth ? position.x : position.x - (moduleWidth * overlapRequired)
      const adjustedY = isFullHeight ? position.y : position.y - (moduleHeight * overlapRequired)
      const adjustedWidth = isFullWidth
        ? contentGrid.width
        : position.x + (cellSize.width * moduleSize.columns) - (moduleWidth * overlapRequired)
      const adjustedHeight = isFullHeight
        ? contentGrid.height
        : position.y + (cellSize.height * moduleSize.rows) - (moduleHeight * overlapRequired)

      if (
        dragX >= adjustedX // inside of left edge
        && adjustedX + moduleWidth <= contentGrid.width // inside of right edge
        && dragX <= adjustedWidth
        && dragY >= adjustedY // inside of top edge
        && adjustedY + moduleHeight <= contentGrid.height // inside of bottom edge
        && dragY <= adjustedHeight
      ){
        dropPosition = position
        break
      }
    }
  }

  return dropPosition
}

type DropData = {
  callbacks: {
    getElementRect: GetElementRect,
  },
  dataset: object,
  dropIndex: number,
  dropTargetEntity: object,
  e: Event,
  height: number,
  itemType: string,
  publicationBookingItems: PublicationBookingItemModel[],
  setState: SetState<DefaultState>,
  stateRef: React.MutableRefObject<DefaultState>,
  styles: ContentGridStyles,
  width: number,
  x: number,
  y: number,
}

type OnDropParams = DropData & {
  callbacks: {
    createPublicationBookingItem: UsePublicationBookingItemPayload['callbacks']['createPublicationBookingItem'],
    updatePublicationBookingItem: UsePublicationBookingItemPayload['callbacks']['updatePublicationBookingItem'],
  },
  publicationBooking: PublicationBookingModel,
  publicationBookingItems: PublicationBookingItemModel[],
  setState: SetState<DefaultState>,
  stateRef: React.MutableRefObject<DefaultState>,
  styles: ContentGridStyles,
  zoom: number,
}

const onDrop = (params: OnDropParams) => {
  const {
    callbacks: {
      createPublicationBookingItem,
      getElementRect,
      updatePublicationBookingItem,
    },
    e: {
      // @ts-expect-error TS2339
      relatedTarget,
      target,
    },
    publicationBooking,
    publicationBookingItems,
    setState,
    stateRef,
    styles,
    zoom,
  } = params

  // @ts-expect-error TS2345
  const dropRect = getElementRect(target)
  const dragRect = getElementRect(relatedTarget)

  const x = dragRect.left - dropRect.left
  const y = dragRect.top - dropRect.top

  const columns = Number(relatedTarget.getAttribute('data-columns'))
  const rows = Number(relatedTarget.getAttribute('data-rows'))

  relatedTarget.classList.remove(styles.draggingOver)
  relatedTarget.classList.remove(styles.dropValid)

  const dropPosition = calculateValidDropPosition({
    dragX: x,
    dragY: y,
    moduleColumns: columns,
    moduleRows: rows,
    stateRef,
  })

  if (dropPosition){
    const bookingItemId = relatedTarget.getAttribute('data-booking-item-id')

    const isValid = validateDropPosition({
      bookingItemId,
      dropPosition,
      publicationBookingItems,
    })

    if (isValid){
      const orderId = Number(relatedTarget.getAttribute('data-order-id'))
      const orderItemId = Number(relatedTarget.getAttribute('data-order-item-id'))

      const publicationBookingItemParams = {
        publication_booking_id: publicationBooking.id,
        x: dropPosition.x / zoom,
        y: dropPosition.y / zoom,
        column: dropPosition.column,
        row: dropPosition.row,
      }

      // New Item on Page
      if (!bookingItemId){
        createPublicationBookingItem({
          ...publicationBookingItemParams,
          order_id: orderId,
          order_item_id: orderItemId,
        }, requestOptions.publicationBookingItem)
      } else {
        if (relatedTarget) relatedTarget.classList.add(styles.hidden)

        updatePublicationBookingItem({
          ...publicationBookingItemParams,
          id: bookingItemId,
        }, requestOptions.publicationBookingItem).then(() => {
          if (relatedTarget) relatedTarget.classList.remove(styles.hidden)
        })
      }
    }
  }

  setState({
    dropPosition: {},
  })
}
type GetElementRect = (target: EventTarget) => { left: number, top: number, right: number, bottom: number }

type OnDropMoveParams = {
  getElementRect: GetElementRect,
  e: Event,
  setState: SetState<DefaultState>,
  stateRef: React.MutableRefObject<DefaultState>,
  styles: ContentGridStyles,
}

const onDropMove = (params: OnDropMoveParams) => {
  const {
    getElementRect,
    e: {
      // @ts-expect-error TS2339
      relatedTarget,
      target,
    },
    setState,
    stateRef,
    styles,
  } = params

  // @ts-expect-error TS2345
  const dropRect = getElementRect(target)
  const dragRect = getElementRect(relatedTarget)

  if (!dragRect || !dropRect){
    return
  }

  const x = dragRect.left - dropRect.left
  const y = dragRect.top - dropRect.top

  const columns = Number(relatedTarget.getAttribute('data-columns'))
  const rows = Number(relatedTarget.getAttribute('data-rows'))

  const dropPosition = calculateValidDropPosition({
    dragX: x,
    dragY: y,
    moduleColumns: columns,
    moduleRows: rows,
    stateRef,
  })

  if (dropPosition){
    relatedTarget.classList.add(styles.dropValid)
    setState({ dropPosition })
  } else {
    relatedTarget.classList.remove(styles.dropValid)
    setState({ dropPosition: {} })
  }
}

type SetupLayoutGridParams = {
  layoutTemplate: {},
  zoom: number,
}

const setupLayoutGrid = (params: SetupLayoutGridParams) => {
  const {
    layoutTemplate: {
      // @ts-expect-error TS2339
      content_grid: contentGrid,
    },
    zoom,
  } = params

  if (contentGrid){
    const {
      columns, height, module_positioning, rows, width, x, y,
    } = contentGrid

    return {
      contentGrid: {
        columns,
        height: height * zoom,
        module_positioning,
        rows,
        width: width * zoom,
        x: x * zoom,
        y: y * zoom,
      },
      cellSize: {
        width: parseFloat(((width / columns) * zoom).toFixed(3)),
        height: parseFloat(((height / rows) * zoom).toFixed(3)),
      },
      dropPosition: {},
    }
  }

  return defaultState
}

type UseContentGridOptions = {
  layoutTemplate: LayoutTemplateModel,
  publicationBooking: PublicationBookingModel,
  publicationBookingItems: PublicationBookingItemModel[],
  styles: ContentGridStyles,
  zoom: number,
}

function useContentGrid(options:UseContentGridOptions) {
  const {
    layoutTemplate,
    publicationBooking,
    publicationBookingItems,
    styles,
    zoom,
  } = options

  const [state, setState] = useSetState(defaultState)
  const { contentGrid, cellSize, dropPosition } = state

  const dropTargetRef = useRef(null)

  const stateRef = useRef(state)
  stateRef.current = state

  const {
    updatedEntities: { publicationBookingItems: publicationBookingItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const publicationBookingItemPayload = usePublicationBookingItem({
    publication_booking_id: publicationBooking.id,
  })

  const {
    callbacks: {
      createPublicationBookingItem,
      updatePublicationBookingItem,
    },
  } = publicationBookingItemPayload

  useDropTarget(dropTargetRef, {}, {
    acceptedTypes: ['orderItem', 'publicationBookingItem'],
    callbacks: {
      dragEnter: (e: Event) => onDragEnter({ e, stateRef, styles }),
      dragLeave: (e: Event) => onDragLeave({ e, styles }),
      dropItem: (data: DropData) => onDrop({
        ...data,
        callbacks: {
          ...data.callbacks,
          createPublicationBookingItem,
          updatePublicationBookingItem,
        },
        publicationBooking,
        publicationBookingItems,
        stateRef,
        setState,
        styles,
        zoom,
      }),
      dropMove: (e: Event, getElementRect: GetElementRect) => onDropMove({
        e, getElementRect, setState, stateRef, styles,
      }),
    },
  }, [layoutTemplate.id, publicationBooking.id, publicationBookingItemsUpdatedAt])

  useEffect(() => {
    if (layoutTemplate.id){
      const layoutGrid = setupLayoutGrid({
        layoutTemplate,
        zoom,
      })

      if (layoutGrid){
        setState(layoutGrid)
      }
    }
  }, [layoutTemplate.id, zoom])

  return {
    contentGrid,
    cellSize,
    dropPosition,
    dropTargetRef,
  }
}

export default useContentGrid
